// External dependencies
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isEmpty, _notNil } from '@/littledash';

// Model types
import type { CustomColumnSettings as CustomColumnSettingsV12 } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { CustomColumnSettingsV16 } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { ID } from '@/model/Common.model';
import type {
  MetadataField,
  MetadataFieldWithValue,
  ModalMeta,
  StudyGroupsMetadataSettings,
} from '@/model/Metadata.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';

// Support modules
import { featuresSelector } from '@/support/Selectors';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { metadataMapToSortedMetadataArray, sanitizeMetadataValue, valueSet } from '@/support/hot';
import { metadataMapToSortedMetadataArrayV16, sanitizeMetadataValueV16, valueSetV16 } from '@/support/hot-v16';
import { RouteService } from '@/support/RouteService';
import { structuredCloneUtil } from '@/utils/browser.utils';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';

// Internal modules
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';

// Relative imports
import { generateColumnData, initialData } from './CreateTreatmentGroups.utils';
import { generateColumnDataV16, initialDataV16 } from './CreateTreatmentGroups.utils.v16';
import './CreateTreatmentGroups.scss';

/**
 Lazy load both table versions to ensure complete isolation between Handsontable v12 and v16.
 V12 and V16 register different cell types, validators, and editors globally in Handsontable.
Static imports would cause both versions to initialize their modules on page load,
 leading to conflicts where V16 validators/editors could affect V12 tables and vice versa.
With lazy loading, only the required version's modules are loaded based on the `hotUpgrade` flag,
 preventing cross-version interference.
 */
const TreatmentGroupsTableV12 = lazy(() => import('./TreatmentGroupsTableV12'));
const TreatmentGroupsTableV16 = lazy(() => import('./TreatmentGroupsTableV16'));

export interface TreatmentGroupKeyedMetadata extends Omit<TreatmentGroup, 'metadata'> {
  metadata: Record<string, MetadataFieldWithValue>;
}

import type Handsontable from 'handsontable-v16';

type HotTableRefUnion = { hotInstance: Handsontable | null } | null;

const mapIncomingGroupData = (groups: Array<ModalMeta<TreatmentGroup>>): Array<TreatmentGroupKeyedMetadata> =>
  groups.map<TreatmentGroupKeyedMetadata>((group) => {
    const groupAnimalCount = group?.animals?.length ?? 0;
    const groupMeta = group?.metadata ?? group?.meta?.data ?? [];
    const metadata = groupMeta.reduce<Record<ID, MetadataFieldWithValue>>((acc, meta) => {
      if (_notNil(meta)) {
        acc[meta.glossary_id ?? meta.id] = { ...meta };
      }
      return acc;
    }, {});
    return { ...group, animals_count: groupAnimalCount, metadata };
  });

const getMetadataToRetain = (groups: Array<TreatmentGroupKeyedMetadata>, isV16: boolean): Set<ID> =>
  groups.reduce<Set<ID>>((acc, group) => {
    Object.values(group.metadata ?? {}).forEach(({ glossary_id: glossaryId, value, field_type: fieldType }) => {
      const sanitizedValue = isV16
        ? sanitizeMetadataValueV16(value, fieldType)
        : sanitizeMetadataValue(value, fieldType);
      const hasValue = isV16 ? valueSetV16(sanitizedValue) : valueSet(sanitizedValue);
      if (hasValue) {
        acc.add(glossaryId);
      }
    });
    return acc;
  }, new Set());

interface CreateTreatmentGroupsProps {
  groups: Array<ModalMeta<TreatmentGroup>>;
  onAddGroups: (groups: Array<TreatmentGroup>) => void;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

const CreateTreatmentGroups: React.FC<CreateTreatmentGroupsProps> = ({ groups, onAddGroups }) => {
  const { hotUpgrade } = useSelector(hotUpgradeSelector);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);

  const [columnData, updateColumnData] = useState<{
    colHeaders: Array<string>;
    columns: Array<CustomColumnSettingsV12 | CustomColumnSettingsV16>;
  }>({
    colHeaders: [],
    columns: [],
  });
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const initialGroupData = useMemo<Array<TreatmentGroupKeyedMetadata>>(
    () => structuredCloneUtil<Array<TreatmentGroupKeyedMetadata>>(hotUpgrade ? initialDataV16 : initialData),
    [hotUpgrade]
  );

  const [groupData, setGroupData] = useState<{ groups: Array<TreatmentGroupKeyedMetadata> }>({
    groups: _isEmpty(groups) ? initialGroupData : mapIncomingGroupData(groups),
  });

  const ref = useRef<HotTableRefUnion>(null);
  const dispatch = useDispatch();

  const validateRows = useCallback(
    async () =>
      new Promise<boolean>((resolve) => {
        const hot = ref.current?.hotInstance;
        if (_notNil(hot)) {
          try {
            hot.validateRows(
              Array.from({ length: hot.countRows() - 1 }).map((_, idx) => idx),
              (valid: boolean) => {
                resolve(valid);
                hot.render();
              }
            );
          } catch {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      }),
    [ref]
  );

  useEffect(() => {
    setGroupData({
      groups: _isEmpty(groups) ? initialGroupData : mapIncomingGroupData(groups),
    });
  }, [groups, initialGroupData]);

  const { entity: groupMetadata, entityLoading: groupMetadataLoading } = useFetchEntity<Array<MetadataField>>({
    entityType: 'glossaryMeta',
    queryParams: {
      filter_type: 'group_meta',
    },
  });

  const { entity: studyGroupsMetadataSettings, entityLoading: studyGroupsMetadataSettingsLoading } = useFetchEntity<
    Array<StudyGroupsMetadataSettings>
  >({
    entityType: 'studyGroups',
  });

  useEffect(() => {
    if (
      columnData.colHeaders.length === 0 &&
      _isEmpty(metadata) &&
      _notNil(groupMetadata) &&
      _notNil(studyGroupsMetadataSettings) &&
      _notNil(groups)
    ) {
      const metadataMap = new Map(groupMetadata?.map((meta) => [meta.id, meta]) ?? []);

      groups.forEach((group) =>
        group?.metadata?.forEach((md) => {
          const { glossary_id: id, options, title, active } = md;
          if (!metadataMap.has(id)) {
            metadataMap.set(id, {
              id,
              glossary_id: id,
              field_type: md.field_type,
              options,
              read_only: md.read_only,
              title,
              created_at: md.created_at,
              updated_at: md.updated_at,
              active,
              slug: md.slug,
              required: md.required ?? false,
            });
          }
        })
      );

      const requiredMetadata = new Map(studyGroupsMetadataSettings?.map(({ id, required }) => [id, required]) ?? []);
      groups.forEach((g) => {
        (g?.metadata ?? []).forEach((m) => {
          const id = m?.glossary_id ?? m?.id;
          if (_notNil(id) && !requiredMetadata.has(id)) {
            requiredMetadata.set(id, false);
          }
        });
      });

      // Use appropriate generator based on hotUpgrade flag
      const generator = hotUpgrade ? generateColumnDataV16 : generateColumnData;
      updateColumnData(
        generator(
          [...requiredMetadata.entries()].map(([metadataId, required]) => ({
            ...metadataMap.get(metadataId),
            required,
          }))
        )
      );
      setMetadata(groupMetadata.filter(({ id }) => !requiredMetadata.has(id)));
    }
  }, [groupMetadata, studyGroupsMetadataSettings, groups, metadata, columnData.colHeaders.length, hotUpgrade]);

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const submitGroups = async () => {
    const valid = await validateRows();
    if (valid && _notNil(onAddGroups)) {
      const metadataGlossaryIdOrder = columnData.columns.reduce<Array<number>>(
        (acc, column) => [...acc, ...(_notNil(column.metaID) ? [column.metaID] : [])],
        []
      );
      const groupsToSubmit = groupData.groups.slice(0, groupData.groups.length - 1);
      const metadataIdsToRetain = getMetadataToRetain(groupsToSubmit, hotUpgrade);

      // Use appropriate metadata mapper based on hotUpgrade flag
      const metadataMapper = hotUpgrade ? metadataMapToSortedMetadataArrayV16 : metadataMapToSortedMetadataArray;
      onAddGroups(
        groupsToSubmit.map((group) => ({
          ...group,
          metadata: metadataMapper(metadataGlossaryIdOrder, metadataIdsToRetain, group.metadata),
        }))
      );

      closeModal();
    }
  };

  const TableComponent = (
    <Suspense fallback={<Loading />}>
      {hotUpgrade ? (
        <TreatmentGroupsTableV16
          groupData={groupData}
          setGroupData={setGroupData}
          columnData={{
            colHeaders: columnData.colHeaders,
            columns: columnData.columns as Array<CustomColumnSettingsV16>,
          }}
          updateColumnData={updateColumnData}
          metadata={metadata}
          setMetadata={setMetadata}
          setIsInvalid={setIsInvalid}
          innerRef={ref}
        />
      ) : (
        <TreatmentGroupsTableV12
          groupData={groupData}
          setGroupData={setGroupData}
          columnData={{
            colHeaders: columnData.colHeaders,
            columns: columnData.columns as Array<CustomColumnSettingsV12>,
          }}
          updateColumnData={updateColumnData}
          metadata={metadata}
          setMetadata={setMetadata}
          setIsInvalid={setIsInvalid}
          innerRef={ref}
        />
      )}
    </Suspense>
  );

  return (
    <div
      style={{ minHeight: '90vh' }}
      data-test-component="CreateTreatmentGroups"
      data-test-element="container"
      className="flex flex-wrap w-90 overflow-hidden center ma3 bg-light-gray br2 mt4 mb-2 relative"
    >
      <div className="flex flex-wrap self-start w-100" style={{ minHeight: 'calc( 90vh - 72px)' }}>
        <div className="ph4 pt4 pb3 relative z-5">
          <h3 className="normal f4 lh-title">Setup treatment groups</h3>
          <p className="lh-copy f5 pv2">
            Click a field to update group information or add a new metadata column.
            <a
              href={RouteService.help('treatment_groups')}
              target="_blank"
              rel="noopener noreferrer"
              className="link blue"
            >
              {' '}
              Read more
            </a>
          </p>
        </div>
        {groupMetadataLoading || studyGroupsMetadataSettingsLoading ? (
          <div className="w-100 h-100">
            <Loading />
          </div>
        ) : (
          <div
            className="ph4 pb4 w-100 ow-spreadsheet-styles"
            style={{ marginTop: '-50px' }}
            data-test-component="CreateTreatmentGroups"
            data-test-element="spreadsheet-container"
          >
            {TableComponent}
          </div>
        )}
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="CreateTreatmentGroups"
        data-test-element="action-container"
      >
        <Button
          onClick={submitGroups}
          testId="save-action"
          disabled={groupMetadataLoading || studyGroupsMetadataSettingsLoading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default CreateTreatmentGroups;
